import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { useForm } from "react-hook-form";
import {
  Page,
  Table,
  type Column,
  Badge,
  Button,
  FilterField,
  FilterPanel,
  FormModal,
  Input,
  Pagination,
  Select,
  Text,
  DateTimeField,
  EmptyState,
} from "@marekl/adminer/components";
import { useConfirm, useDebouncedSearchFilter } from "@marekl/adminer/hooks";
import { apiErrorToFormErrors, rootErrorMessage } from "@marekl/adminer/forms";
import { notifySuccess } from "@marekl/adminer/api";
import { useIsAdmin } from "../../auth/useAuth";
import { useSenderList } from "../../lib/queries/senders";
import { isPhoneNumber } from "../../lib/sms";
import {
  useCreateSuppression,
  useDeleteSuppression,
  useSuppressionList,
  type Suppression,
  type SuppressionCreate,
} from "../../lib/queries/suppressions";
import { usePagedList } from "../../lib/usePagedList";
import { RefreshButton } from "../../components/RefreshButton";

const REASON_LABELS: Record<string, string> = {
  unsubscribe: "Unsubscribe",
  hard_bounce: "Hard bounce",
  complaint: "Complaint",
  manual: "Manual",
};

const SCOPE_LABELS: Record<string, string> = {
  marketing: "Marketing only",
  all: "All mail",
};

const REASON_OPTIONS = [
  { value: "", label: "All" },
  ...Object.entries(REASON_LABELS).map(([value, label]) => ({ value, label })),
];

interface SuppressionFormValues {
  channel: "email" | "sms";
  address: string;
  /** "" = every sender */
  sender: string;
  scope: NonNullable<SuppressionCreate["scope"]>;
  reason: NonNullable<SuppressionCreate["reason"]>;
}

function AddSuppressionModal({ onClose }: { onClose: () => void }) {
  const create = useCreateSuppression();
  const senders = useSenderList();
  const {
    register,
    handleSubmit,
    setError,
    watch,
    formState: { errors },
  } = useForm<SuppressionFormValues>({
    mode: "onTouched",
    defaultValues: {
      channel: "email",
      address: "",
      sender: "",
      scope: "marketing",
      reason: "manual",
    },
  });
  // A number is stored the way a send normalizes it, so the two match.
  const isSMS = watch("channel") === "sms";

  async function onSubmit(values: SuppressionFormValues) {
    try {
      await create.mutateAsync({
        channel: values.channel,
        address: values.address.trim(),
        sender: values.sender || undefined,
        scope: values.scope,
        reason: values.reason,
      });
      notifySuccess("The recipient was suppressed.");
      onClose();
    } catch (err) {
      apiErrorToFormErrors<SuppressionFormValues>(err, setError, {
        fallbackMessage: "Saving failed. Please try again.",
      });
    }
  }

  return (
    <FormModal
      open
      title="Suppress a recipient"
      onClose={onClose}
      busy={create.isPending}
      error={rootErrorMessage(errors)}
      submitLabel="Suppress"
      onSubmit={handleSubmit(onSubmit)}
    >
      <Select
        label="Channel"
        error={errors.channel?.message}
        {...register("channel")}
      >
        <option value="email">E-mail</option>
        <option value="sms">SMS</option>
      </Select>
      <Input
        label={isSMS ? "Phone number" : "Email address"}
        type={isSMS ? "tel" : "email"}
        required
        placeholder={isSMS ? "+420777123456" : "address@example.com"}
        hint={
          isSMS
            ? "In E.164 form — it is stored the way a send normalizes it, so the two match."
            : undefined
        }
        error={errors.address?.message}
        {...register("address", {
          required: "Required field",
          validate: (v, values) =>
            values.channel !== "sms" ||
            isPhoneNumber(v) ||
            "Must be a phone number in E.164 form",
        })}
      />
      <Select
        label="Sender"
        hint="An unsubscribe only stops that sender's mail. “All senders” is for complaints and manual blocks."
        error={errors.sender?.message}
        {...register("sender", {
          validate: (value, values) =>
            value !== "" ||
            values.reason !== "unsubscribe" ||
            "Unsubscribes are per sender — choose a sender.",
        })}
      >
        <option value="">All senders</option>
        {(senders.data ?? []).map((s) => (
          <option key={s.key} value={s.key}>
            {s.name} ({s.key})
          </option>
        ))}
      </Select>
      <Select
        label="Scope"
        hint="“Marketing only” still lets transactional messages through."
        error={errors.scope?.message}
        {...register("scope")}
      >
        <option value="marketing">Marketing only</option>
        <option value="all">All mail</option>
      </Select>
      <Select
        label="Reason"
        error={errors.reason?.message}
        {...register("reason", { deps: ["sender"] })}
      >
        <option value="manual">Manual</option>
        {/* An SMS never carries an unsubscribe link, so nothing can generate
            this reason and the server refuses it (ErrReasonNotForChannel).
            Offering it only to have the submit fail is worse than not
            offering it. */}
        {!isSMS && <option value="unsubscribe">Unsubscribe</option>}
        <option value="complaint">Complaint</option>
      </Select>
    </FormModal>
  );
}

/** Everything this page shows, for the header's refresh button. */
const REFRESH_KEYS = [["suppressions"], ["senders"]];

export function SuppressionsPage() {
  const isAdmin = useIsAdmin();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addOpen, setAddOpen] = useState(false);
  const confirm = useConfirm();
  const del = useDeleteSuppression();
  const senders = useSenderList();

  const reason = searchParams.get("reason") ?? "";
  const sender = searchParams.get("sender") ?? "";
  const q = searchParams.get("q") ?? "";

  const { data, isLoading, error, refetch, pagination, filterReset, summary } =
    usePagedList(
      useSuppressionList,
      {
        reason: reason || undefined,
        sender: sender || undefined,
        q: q || undefined,
      },
      {
        searchParams,
        setSearchParams,
        nounForms: ["recipient", "recipients", "recipients"],
      },
    );

  function applyFilter(key: string, value: string) {
    filterReset((p) => {
      if (value) {
        p.set(key, value);
      } else {
        p.delete(key);
      }
    });
  }

  const { draft: qDraft, setDraft: setQDraft } = useDebouncedSearchFilter({
    value: q,
    onCommit: (next) => applyFilter("q", next),
  });

  const items = data?.items ?? [];

  function confirmDelete(suppression: Suppression) {
    void confirm({
      title: "Remove suppression",
      description: (
        <>
          Really remove the suppression for{" "}
          <strong>{suppression.address}</strong>
          {suppression.sender ? (
            <>
              {" "}from sender <strong>{suppression.sender}</strong>
            </>
          ) : (
            " for all senders"
          )}
          ? Messages to this recipient will be delivered again.
        </>
      ),
      confirmLabel: "Remove",
      variant: "danger",
      onConfirm: async () => {
        await del.mutateAsync({
          channel: suppression.channel,
          address: suppression.address,
          sender: suppression.sender,
        });
      },
    }).then((confirmed) => {
      if (confirmed) notifySuccess("The suppression was removed.");
    });
  }

  const columns: Column<Suppression>[] = [
    {
      key: "channel",
      header: "Channel",
      render: (row) => (
        <Badge variant="info">{row.channel === "sms" ? "SMS" : "E-mail"}</Badge>
      ),
    },
    {
      key: "address",
      header: "Recipient",
      primary: true,
      render: (row) => row.address,
    },
    {
      key: "sender",
      header: "Sender",
      render: (row) => row.sender ?? <Text variant="muted">All senders</Text>,
    },
    {
      key: "scope",
      header: "Scope",
      render: (row) => (
        <Badge variant={row.scope === "all" ? "err" : "warn"}>
          {SCOPE_LABELS[row.scope] ?? row.scope}
        </Badge>
      ),
    },
    {
      key: "reason",
      header: "Reason",
      render: (row) => REASON_LABELS[row.reason] ?? row.reason,
    },
    {
      key: "created_at",
      header: "Suppressed at",
      render: (row) => (
        <Text variant="mono">
          <DateTimeField record={row} source="created_at" />
        </Text>
      ),
    },
    ...(isAdmin
      ? [
          {
            key: "actions",
            header: "",
            render: (row: Suppression) => (
              <Table.Actions>
                <Table.Action variant="danger" onClick={() => confirmDelete(row)}>
                  Remove
                </Table.Action>
              </Table.Actions>
            ),
          } satisfies Column<Suppression>,
        ]
      : []),
  ];

  return (
    <>
      <Page>
        <Page.Header
          title="Suppressions"
          subtitle="addresses that mail is not delivered to"
          subtitleVariant="tagline"
          actions={
            <>
              <RefreshButton queryKeys={REFRESH_KEYS} />
              {isAdmin && (
                <Button variant="primary" onClick={() => setAddOpen(true)}>
                  <Plus size={16} aria-hidden="true" />
                  Suppress address
                </Button>
              )}
            </>
          }
        />
        <Page.Content stack gap="lg">
          <FilterPanel>
            <FilterField>
              <Select
                label="Reason"
                value={reason}
                onChange={(e) => applyFilter("reason", e.target.value)}
              >
                {REASON_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField>
              <Select
                label="Sender"
                hint="Includes suppressions for all senders"
                value={sender}
                onChange={(e) => applyFilter("sender", e.target.value)}
              >
                <option value="">All</option>
                {(senders.data ?? []).map((s) => (
                  <option key={s.key} value={s.key}>
                    {s.name} ({s.key})
                  </option>
                ))}
              </Select>
            </FilterField>
            <FilterField wide>
              <Input
                label="Search"
                type="search"
                value={qDraft}
                placeholder="part of an address"
                onChange={(e) => setQDraft(e.target.value)}
              />
            </FilterField>
          </FilterPanel>

          <Table<Suppression>
            striped
            cards
            columns={columns}
            rows={items}
            rowKey={(r) => `${r.channel}:${r.address}:${r.sender ?? ""}`}
            isLoading={isLoading}
            error={error}
            onRetry={refetch}
            empty={
              <EmptyState
                title="No suppressions"
                description="No records match the selected filters."
              />
            }
          />
        </Page.Content>
        <Page.Footer>
          <Pagination {...pagination} summary={summary} />
        </Page.Footer>
      </Page>

      {addOpen && <AddSuppressionModal onClose={() => setAddOpen(false)} />}
    </>
  );
}
